/**
 * Per-bird pop scaling so portrait assets (1024² with padding / gold ring) fill tree holes evenly.
 */

/** Fraction of the hole diameter filled by visible artwork (0–1). */
export const BEYOND_BIRD_POP_FILL_RATIO = 0.94;

/** Downsample size for alpha bbox scan (faster load). */
const MEASURE_SAMPLE_SIZE = 256;

/** @type {Map<number, { scale: number, originX: string, originY: string }>} */
const styleCache = new Map();

/**
 * @param {HTMLImageElement} img
 * @returns {{ scale: number, originX: string, originY: string }}
 */
export function measureBirdPopVisualStyle(img) {
  const w = img.naturalWidth;
  const h = img.naturalHeight;
  if (!w || !h) {
    throw new Error('measureBirdPopVisualStyle: image has no natural dimensions');
  }

  const canvas = document.createElement('canvas');
  const sample = MEASURE_SAMPLE_SIZE;
  canvas.width = sample;
  canvas.height = sample;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) {
    throw new Error('measureBirdPopVisualStyle: 2d context unavailable');
  }
  ctx.drawImage(img, 0, 0, sample, sample);
  const data = ctx.getImageData(0, 0, sample, sample).data;

  let minX = sample;
  let minY = sample;
  let maxX = 0;
  let maxY = 0;

  for (let y = 0; y < sample; y += 1) {
    for (let x = 0; x < sample; x += 1) {
      const i = (y * sample + x) * 4;
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const a = data[i + 3];
      const isVisible = a > 28 && r + g + b > 36;
      if (!isVisible) continue;
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
  }

  if (maxX <= minX || maxY <= minY) {
    return { scale: 1, originX: '50%', originY: '50%' };
  }

  const bboxW = maxX - minX + 1;
  const bboxH = maxY - minY + 1;
  const contentMax = Math.max(bboxW, bboxH);
  const scale = (sample / contentMax) * BEYOND_BIRD_POP_FILL_RATIO;
  const centerX = (minX + maxX) / 2;
  const centerY = (minY + maxY) / 2;
  const originX = `${(centerX / sample) * 100}%`;
  const originY = `${(centerY / sample) * 100}%`;

  return { scale, originX, originY };
}

/**
 * @param {number} birdId
 * @param {HTMLImageElement} img
 * @returns {{ scale: number, originX: string, originY: string }}
 */
export function getCachedBirdPopVisualStyle(birdId, img) {
  if (!Number.isInteger(birdId) || birdId < 1 || birdId > 7) {
    throw new Error(`getCachedBirdPopVisualStyle: invalid birdId (${String(birdId)})`);
  }
  if (styleCache.has(birdId)) {
    return styleCache.get(birdId);
  }
  const style = measureBirdPopVisualStyle(img);
  styleCache.set(birdId, style);
  return style;
}
